import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Container, Box, Typography, Button, TextField, CircularProgress, Alert, Paper, Grid, Dialog, DialogContent, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  getShare,
  requestVerificationCode,
  validateVerificationCode,
  requestDownload,
  getDownloadStatus,
  getShareMedia,
  shareErrorMessage,
} from '../../services/api/shares';

// SS-437: Canvas-based view-only image renderer.
// Draws the presigned URL directly into a tainted canvas so the lightbox
// does not expose the direct S3 URL in an <img src> attribute.
// Tainting is intentional: drawImage succeeds for display, but
// toDataURL()/getImageData() throw SecurityError (bonus deterrence).
// No fetch()/blob: URL — CSP img-src does not allow blob: scheme.
const ViewOnlyCanvasImage = ({ src, alt, sx }) => {
  const canvasRef = React.useRef(null);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    let cancelled = false;
    const image = new Image();
    image.onload = () => {
      if (cancelled || !canvasRef.current) return;
      const canvas = canvasRef.current;
      canvas.width = image.naturalWidth;
      canvas.height = image.naturalHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        setStatus('error');
        return;
      }
      ctx.drawImage(image, 0, 0);
      setStatus('ready');
    };
    image.onerror = () => {
      if (!cancelled) setStatus('error');
    };
    image.src = src;
    return () => {
      cancelled = true;
      image.src = '';
    };
  }, [src]);

  if (status === 'error') {
    return <Alert severity="error" aria-live="polite">Unable to render protected preview.</Alert>;
  }

  return (
    <Box sx={{ position: 'relative', display: 'inline-block', ...sx }}>
      <canvas
        ref={canvasRef}
        role="img"
        aria-label={alt || 'Protected shared image'}
        draggable={false}
        onContextMenu={(e) => e.preventDefault()}
        onMouseDown={(e) => e.preventDefault()}
        style={{ maxWidth: '100%', maxHeight: '85vh', objectFit: 'contain' }}
      />
      {status === 'loading' && (
        <Box sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <CircularProgress aria-label="Loading protected image…" />
        </Box>
      )}
    </Box>
  );
};

const SharePage = () => {
  const { id } = useParams();
  const [step, setStep] = useState('request');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [meta, setMeta] = useState(null);
  const [downloadUrl, setDownloadUrl] = useState('');
  const [taskId, setTaskId] = useState(null);
  const [downloadToken, setDownloadToken] = useState(null);
  const [downloadError, setDownloadError] = useState(null);
  const [polling, setPolling] = useState(false);

  const [media, setMedia] = useState([]);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [mediaError, setMediaError] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [blurOverlay, setBlurOverlay] = useState(false);
  const [storeHint, setStoreHint] = useState(null);

  const canDownload = Boolean(permissions?.download) && !meta?.viewOnly;

  // SS-426: Block keyboard shortcuts in view-only mode
  const handleKeyDown = useCallback((e) => {
    if (!meta?.viewOnly) return;
    if ((e.ctrlKey || e.metaKey) && (
      e.key === 's' ||
      e.key === 'p' ||
      e.key === 'u' ||
      (e.shiftKey && e.key === 'S')
    )) {
      e.preventDefault();
      e.stopPropagation();
    }
  }, [meta?.viewOnly]);

  // SS-426: Attach view-only deterrent listeners
  useEffect(() => {
    if (!meta?.viewOnly || step !== 'done') return;

    // Keyboard shortcuts
    document.addEventListener('keydown', handleKeyDown, true);

    // Copy event suppression (AC2)
    const handleCopy = (e) => {
      e.preventDefault();
      if (e.clipboardData) e.clipboardData.clearData();
    };
    document.addEventListener('copy', handleCopy, true);

    // Visibilitychange blur overlay (AC3)
    const handleVisibility = () => {
      if (document.hidden) {
        setBlurOverlay(true);
      } else {
        // Brief delay before removing overlay so user sees it was triggered
        setTimeout(() => setBlurOverlay(false), 300);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility, true);

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('copy', handleCopy, true);
      document.removeEventListener('visibilitychange', handleVisibility, true);
    };
  }, [meta?.viewOnly, step, handleKeyDown]);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const res = await getShare(id);
        if (res.success && isMounted) {
          setMeta(res.data);
        } else if (isMounted) {
          setError(shareErrorMessage(res.status, res.data));
        }
      } catch (e) {
        if (isMounted) setError('Unable to load this secure share. Please try again.');
      }
    })();
    return () => { isMounted = false; };
  }, [id]);

  // Fetch media thumbnails after successful OTP validation
  useEffect(() => {
    if (step !== 'done' || !meta?.shareId) return;
    setMediaLoading(true);
    setMediaError(null);
    getShareMedia(meta.shareId)
      .then((res) => {
        if (res.success) setMedia(res.data?.media || []);
        else setMediaError('Unable to load shared media. Please try refreshing the page.');
      })
      .catch((err) => {
        console.error('Failed to load media:', err);
        setMediaError('Unable to load shared media. Please try refreshing the page.');
      })
      .finally(() => setMediaLoading(false));
  }, [step, meta?.shareId]);

  const requestCode = async () => {
    setLoading(true); setError(null);
    try {
      const res = await requestVerificationCode(id);
      if (res.success) {
        setStep('validate');
      } else {
        setError(shareErrorMessage(res.status, res.data));
      }
    } catch (e) { setError('Unable to send verification code. Please try again.'); }
    finally { setLoading(false); }
  };

  const validate = async () => {
    setLoading(true); setError(null);
    try {
      const res = await validateVerificationCode(id, code);
      if (res.success) {
        setPermissions(res.data.permissions);
        if (res.data.storeAccountDetected && res.data.storeLoginUrl) {
          setStoreHint(res.data.storeLoginUrl);
        }
        setStep('done');
      } else {
        setError(shareErrorMessage(res.status, res.data));
      }
    } catch (e) { setError('Unable to validate code. Please try again.'); }
    finally { setLoading(false); }
  };

  const handleDownload = async () => {
    if (!canDownload) return;
    setLoading(true); setError(null); setDownloadError(null);
    try {
      // SS-370 B2: NEVER send userId/username — backend derives identity from shareId
      const res = await requestDownload({
        distributionId: meta?.distributionId,
        email: meta?.email,
        shareId: id,
        setInfo: meta?.setInfo,
      });
      if (res.success) {
        const data = res.data;
        if (data.url) {
          setDownloadUrl(data.url);
          window.location.assign(data.url);
        } else if (data.status === 'processing' && data.taskId) {
          setTaskId(data.taskId);
          setDownloadToken(data.ownerToken || null);
          setPolling(true);
        }
      } else {
        setError(shareErrorMessage(res.status, res.data));
      }
    } catch (e) {
      setError('Unable to start download. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Polling effect for async processing
  useEffect(() => {
    if (!polling || !taskId) return;
    let cancelled = false;
    const MAX_POLL_MS = 5 * 60 * 1000; // 5 minutes max
    const pollStart = Date.now();
    const interval = setInterval(async () => {
      try {
        const elapsed = Date.now() - pollStart;
        if (elapsed > MAX_POLL_MS && !cancelled) {
          setDownloadError('Download is taking too long. Please try generating again.');
          setPolling(false);
          clearInterval(interval);
          return;
        }
        const res = await getDownloadStatus(taskId, downloadToken);
        if (!res.success) return;
        if (res.data.status === 'READY' && res.data.url && !cancelled) {
          setDownloadUrl(res.data.url);
          setDownloadToken(null);
          setPolling(false);
          clearInterval(interval);
          window.location.assign(res.data.url);
        } else if (res.data.status === 'ERROR' && !cancelled) {
          setDownloadError(res.data.error || 'Download generation failed. Please try again later.');
          setPolling(false);
          clearInterval(interval);
        }
      } catch (_) { /* ignore transient errors */ }
    }, 2000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [polling, taskId, downloadToken]);

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      {/* SS-426: Print media query — hide shared media in printouts */}
      <style>{`
        @media print {
          [data-ss-viewonly="true"] { display: none !important; }
          body::after {
            content: 'This content is view-only and cannot be printed.';
            display: block;
            text-align: center;
            padding: 2rem;
            font-size: 1.2rem;
            color: #666;
          }
        }
      `}</style>
      <Paper elevation={4} sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom>Secure Share</Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }} aria-live="polite">{error}</Alert>}
        {step === 'request' && (
          <Box>
            <Typography gutterBottom>
              {meta?.userName ? `${meta.userName} has shared secure content with you.` : 'You have been granted access.'}
            </Typography>
            <Typography gutterBottom>Click the button below to receive a verification code via email.</Typography>
            <Button variant="contained" onClick={requestCode} disabled={loading} aria-label={loading ? 'Sending verification code…' : undefined}>
              {loading ? <CircularProgress size={24} aria-hidden="true" /> : 'Send Verification Code'}
            </Button>
          </Box>
        )}
        {step === 'validate' && (
          <Box>
            <Typography gutterBottom>Enter the 6-digit code sent to your email.</Typography>
            <TextField fullWidth label="Verification Code" value={code} onChange={e => setCode(e.target.value)} inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', spellCheck: false }} autoComplete="one-time-code" name="otp" sx={{ mb: 2 }} />
            <Button variant="contained" onClick={validate} disabled={loading || code.length < 6} aria-label={loading ? 'Validating verification code…' : undefined}>
              {loading ? <CircularProgress size={24} aria-hidden="true" /> : 'Validate'}
            </Button>
          </Box>
        )}
        {step === 'done' && permissions && (
          <Box>
            <Alert severity="success" sx={{ mb: 2 }} aria-live="polite">Access granted.</Alert>
            {storeHint && (
              <Alert severity="info" sx={{ mb: 2 }} aria-live="polite">
                It looks like you have a ShieldShot Store account.{' '}
                <a href={storeHint} target="_blank" rel="noopener noreferrer">
                  Log in to the store
                </a>{' '}
                to access your full media library.
              </Alert>
            )}
            {meta?.viewOnly && (
              <Alert severity="info" sx={{ mb: 2 }} aria-live="polite">
                This link is view-only. Downloads are disabled by the sender.
              </Alert>
            )}
            <Box sx={{ mb: 2 }}>
              <Typography component="span" sx={{ mr: 2 }}>View Enabled: {permissions.view ? 'Yes' : 'No'}</Typography>
              <Typography component="span">Download Enabled: {canDownload ? 'Yes' : 'No'}</Typography>
            </Box>
            {mediaLoading ? (
              <Box sx={{ mb: 2, textAlign: 'center' }}>
                <CircularProgress size={24} aria-label="Loading media…" />
                <Typography variant="body2" sx={{ mt: 1 }} color="text.secondary">
                  Loading shared media…
                </Typography>
              </Box>
            ) : mediaError ? (
              <Alert severity="error" sx={{ mb: 2 }} aria-live="polite">{mediaError}</Alert>
            ) : media.length > 0 ? (
              <Box sx={{ mb: 2, position: 'relative' }} data-ss-viewonly={meta?.viewOnly ? 'true' : undefined}>
                {/* SS-426 AC3: Blur overlay when tab/window loses focus */}
                {blurOverlay && (
                  <Box sx={{
                    position: 'absolute',
                    inset: 0,
                    zIndex: 10,
                    backdropFilter: 'blur(12px)',
                    backgroundColor: 'rgba(0,0,0,0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <Typography variant="body1" color="white" fontWeight="bold">
                      View-only content — content is hidden when this tab loses focus
                    </Typography>
                  </Box>
                )}
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Shared Media ({media.length})</Typography>
                <Grid container spacing={1}>
                   {media.map((item, idx) => {
                     const thumbSrc = item.thumbnailUrl || item.optimizedUrl;
                     const isImage = item.mediaType === 'image';
                     // SS-437: View-only images open canvas lightbox; view-only videos remain non-clickable
                     const canClick = !!item.optimizedUrl && (isImage || !meta?.viewOnly);
                     return (
                       <Grid item xs={12} sm={6} key={item.filename || idx}>
                          <Paper
                            sx={{ p: 1, textAlign: 'center', cursor: canClick ? 'pointer' : 'default', touchAction: meta?.viewOnly ? 'manipulation' : 'auto' }}
                            variant="outlined"
                            onClick={canClick ? () => setLightboxIndex(idx) : undefined}
                            role={canClick ? 'button' : undefined}
                            tabIndex={canClick ? 0 : undefined}
                            aria-label={canClick ? `View ${item.filename || `media ${idx + 1}`} fullscreen` : undefined}
                            onKeyDown={canClick ? (e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setLightboxIndex(idx);
                              }
                            } : undefined}
                            // SS-426 AC4: Mobile long-press deterrence
                            onTouchStart={meta?.viewOnly ? (e) => {
                              e.preventDefault();
                            } : undefined}
                          >
                          {isImage ? (
                            <Box
                              component="img"
                              src={thumbSrc}
                              alt={item.filename || `Media ${idx + 1}`}
                              width={300}
                              height={300}
                              loading="lazy"
                              sx={{
                                width: '100%',
                                maxHeight: 300,
                                objectFit: 'contain',
                                borderRadius: 1,
                                userSelect: meta?.viewOnly ? 'none' : 'auto',
                                WebkitUserSelect: meta?.viewOnly ? 'none' : 'auto',
                                WebkitTouchCallout: meta?.viewOnly ? 'none' : 'auto',
                                pointerEvents: 'auto',
                              }}
                              draggable={false}
                              onContextMenu={meta?.viewOnly ? (e) => e.preventDefault() : undefined}
                              onMouseDown={meta?.viewOnly ? (e) => e.preventDefault() : undefined}
                            />
                          ) : item.mediaType === 'video' ? (
                            <Box
                              component="video"
                              src={thumbSrc}
                              controls={false}
                              aria-label={item.filename || 'Shared video'}
                              controlsList={meta?.viewOnly ? 'nodownload nofullscreen nopictureinpicture' : 'nodownload'}
                              disablePictureInPicture={meta?.viewOnly || undefined}
                              sx={{
                                width: '100%',
                                maxHeight: 300,
                                borderRadius: 1,
                                userSelect: meta?.viewOnly ? 'none' : 'auto',
                                WebkitUserSelect: meta?.viewOnly ? 'none' : 'auto',
                                WebkitTouchCallout: meta?.viewOnly ? 'none' : 'auto',
                                pointerEvents: 'auto',
                              }}
                              draggable={false}
                              onContextMenu={meta?.viewOnly ? (e) => e.preventDefault() : undefined}
                              onMouseDown={meta?.viewOnly ? (e) => e.preventDefault() : undefined}
                            />
                          ) : (
                            <Box sx={{ bgcolor: 'grey.100', p: 4, borderRadius: 1 }}>
                              <Typography color="text.secondary">
                                {item.filename || `Media ${idx + 1}`}
                              </Typography>
                            </Box>
            )}
          </Paper>
        </Grid>
      );
    })}
                </Grid>
                {/* Lightbox dialog for viewing optimized media — outside map to render single instance */}
                {lightboxIndex !== null && media[lightboxIndex] && (
                  <Dialog
                    open
                    onClose={() => setLightboxIndex(null)}
                    maxWidth="lg"
                    fullWidth
                    PaperProps={{ sx: { bgcolor: 'rgba(0,0,0,0.92)', borderRadius: 2 } }}
                  >
                    <DialogContent sx={{ p: 0, position: 'relative', textAlign: 'center' }}>
                      <IconButton
                        onClick={() => setLightboxIndex(null)}
                        aria-label="Close preview"
                        sx={{ position: 'absolute', top: 8, right: 8, color: 'white', bgcolor: 'rgba(0,0,0,0.4)', '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' }, zIndex: 1 }}
                      >
                        <CloseIcon />
                      </IconButton>
                      {media[lightboxIndex].mediaType === 'image' ? (
                        meta?.viewOnly ? (
                          <ViewOnlyCanvasImage
                            src={media[lightboxIndex].optimizedUrl || media[lightboxIndex].thumbnailUrl}
                            alt={media[lightboxIndex].filename || 'Shared media'}
                          />
                        ) : (
                          <Box
                            component="img"
                            src={media[lightboxIndex].optimizedUrl || media[lightboxIndex].thumbnailUrl}
                            alt={media[lightboxIndex].filename || 'Shared media'}
                            sx={{
                              maxWidth: '100%',
                              maxHeight: '85vh',
                              objectFit: 'contain',
                            }}
                            draggable={false}
                          />
                        )
                      ) : (
                        <Box
                          component="video"
                          src={media[lightboxIndex].optimizedUrl || media[lightboxIndex].thumbnailUrl}
                          controls
                          aria-label={media[lightboxIndex].filename || 'Shared video'}
                          controlsList={meta?.viewOnly ? 'nodownload nofullscreen nopictureinpicture' : undefined}
                          disablePictureInPicture={meta?.viewOnly || undefined}
                          sx={{
                            maxWidth: '100%',
                            maxHeight: '85vh',
                            userSelect: meta?.viewOnly ? 'none' : 'auto',
                            WebkitUserSelect: meta?.viewOnly ? 'none' : 'auto',
                            pointerEvents: 'auto',
                          }}
                          draggable={false}
                          onContextMenu={meta?.viewOnly ? (e) => e.preventDefault() : undefined}
                        />
                      )}
                    </DialogContent>
                  </Dialog>
                )}
              </Box>
            ) : null}
            {canDownload ? (
              <Box sx={{ mt: 2 }}>
                <Button variant="contained" onClick={handleDownload} disabled={loading} aria-label={loading ? 'Generating download…' : undefined}>
                  {loading ? <CircularProgress size={24} aria-hidden="true" /> : 'Generate Download'}
                </Button>
                {polling && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Preparing your download… this can take a minute. We'll start it automatically when ready.
                  </Typography>
                )}
                {downloadError && (
                  <Alert severity="error" sx={{ mt: 2 }} aria-live="polite">{downloadError}</Alert>
                )}
                {downloadUrl && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Your download will start automatically. If it doesn't, click Generate Download again — the link expires after 60 seconds.
                  </Typography>
                )}
              </Box>
            ) : (
              <Alert severity="warning" sx={{ mt: 2 }} aria-live="polite">
                {meta?.viewOnly
                  ? 'This share is view-only. Downloads are not permitted.'
                  : 'Downloads are not available for this share.'}
              </Alert>
            )}
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default SharePage;
